import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import type { Action, Module } from '@mygmao/shared';
import {
  Activity,
  AlertOctagon,
  BarChart3,
  Bell,
  Briefcase,
  Building,
  Building2,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  FileSignature,
  FileSpreadsheet,
  FileText,
  HardHat,
  History,
  LayoutDashboard,
  LineChart,
  ListChecks,
  Mail,
  Settings,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  UserCog,
  Users,
  Wrench,
  X,
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantSettings } from '../../contexts/TenantSettingsContext';

/**
 * Sidebar applicative (US-DASH-03).
 *
 *  - Filtrée par permissions via `usePermissions().hasModuleAccess()`.
 *  - 2 menus distincts :
 *      • Admin tenant : Dashboard + 7 modules métier + Administration (sous-menu)
 *      • Super-admin plateforme : Tenants + Audit cross-tenant
 *  - L'entrée "Administration" est un groupe (Utilisateurs / Rôles / Audit /
 *    Formulaires), expansible quand la sidebar est étendue.
 *  - Repliable (w-64 ↔ w-16) sur desktop + overlay mobile.
 */

interface SidebarProps {
  isOpen: boolean;
  isCollapsed: boolean;
  onClose(): void;
  onToggleCollapse(): void;
}

interface LeafEntry {
  module: Module | null;
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface GroupChild {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Permission optionnelle requise pour afficher cette entrée. */
  requiredAction?: Action;
  /** Si défini, l'entrée n'est visible que si l'user a `module.requiredAction`. */
  requiredModule?: Module;
}

interface GroupEntry {
  module: Module;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Préfixes d'URL qui déclenchent l'état "actif" du parent. */
  matchPrefix: string;
  /** Routes additionnelles qui doivent activer le groupe (hors matchPrefix). */
  matchPaths?: string[];
  children: GroupChild[];
}

type MenuItem = LeafEntry | GroupEntry;

const TENANT_MENU: MenuItem[] = [
  { module: 'dashboard', to: '/dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
  { module: 'batiments', to: '/batiments', label: 'Bâtiments', icon: Building2 },
  { module: 'equipements', to: '/equipements', label: 'Équipements', icon: Wrench },
  { module: 'equipements', to: '/mes-affectations', label: 'Mes affectations', icon: UserCog },
  { module: 'services', to: '/services', label: 'Services', icon: Building },
  {
    module: 'interventions',
    label: 'Interventions',
    icon: HardHat,
    matchPrefix: '/interventions',
    matchPaths: ['/mes-validations', '/mes-interventions', '/prestataires', '/contrats'],
    children: [
      { to: '/interventions', label: 'Toutes les interventions', icon: ClipboardList },
      { to: '/interventions/agenda', label: 'Agenda', icon: CalendarDays },
      { to: '/interventions/dashboard', label: 'Dashboard', icon: BarChart3 },
      { to: '/interventions/urgences', label: 'Urgences', icon: AlertOctagon },
      { to: '/mes-validations', label: 'Mes validations', icon: ShieldCheck },
      { to: '/mes-interventions', label: 'Mes interventions', icon: ListChecks },
      {
        to: '/contrats',
        label: 'Contrats',
        icon: FileSignature,
        requiredModule: 'interventions',
      },
      {
        to: '/prestataires',
        label: 'Prestataires',
        icon: Briefcase,
        requiredModule: 'interventions',
        requiredAction: 'update',
      },
    ],
  },
  { module: 'personnels', to: '/personnels', label: 'Personnels', icon: Users },
  { module: 'notifications', to: '/notifications', label: 'Notifications', icon: Bell },
  {
    module: 'reporting',
    label: 'Reporting',
    icon: BarChart3,
    matchPrefix: '/reporting',
    children: [
      { to: '/reporting', label: 'Vue globale', icon: LineChart },
      { to: '/reporting/equipements', label: 'Équipements', icon: Wrench },
      { to: '/reporting/infrastructure', label: 'Infrastructure', icon: Building2 },
      { to: '/reporting/interventions', label: 'Interventions', icon: HardHat },
      { to: '/reporting/prestataires', label: 'Rapport prestataires', icon: FileSpreadsheet },
      { to: '/reporting/historique', label: 'Historique exports', icon: History },
    ],
  },
  {
    module: 'administration',
    label: 'Administration',
    icon: Settings,
    matchPrefix: '/admin/',
    children: [
      { to: '/admin/utilisateurs', label: 'Utilisateurs', icon: Users },
      { to: '/admin/roles', label: 'Rôles & permissions', icon: Shield },
      { to: '/admin/parametres', label: 'Paramètres', icon: SlidersHorizontal },
      { to: '/admin/audit-logs', label: "Journaux d'audit", icon: Activity },
      { to: '/admin/formulaires', label: 'Formulaires dynamiques', icon: FileText },
    ],
  },
];

const PLATFORM_MENU: LeafEntry[] = [
  { module: null, to: '/platform/tenants', label: 'Tenants', icon: Building2 },
  { module: null, to: '/platform/demo-requests', label: 'Demandes de démo', icon: Mail },
  { module: null, to: '/platform/audit', label: 'Audit cross-tenant', icon: BarChart3 },
];

function isGroup(item: MenuItem): item is GroupEntry {
  return 'children' in item;
}

export function Sidebar({ isOpen, isCollapsed, onClose, onToggleCollapse }: SidebarProps) {
  const { can, hasModuleAccess, isPlatformAdmin } = usePermissions();
  const { user } = useAuth();
  const { tenant: tenantSettings } = useTenantSettings();
  const logoUrl = tenantSettings?.logo_signed_url ?? null;
  const tenantLabel = user?.tenant?.nom ?? (isPlatformAdmin ? 'Plateforme' : 'mygmao.pro');
  const tenantInitials =
    user?.tenant?.code?.slice(0, 2).toUpperCase() ?? (isPlatformAdmin ? 'PL' : 'M');
  const menu: MenuItem[] = isPlatformAdmin ? PLATFORM_MENU : TENANT_MENU;
  const visible = menu.filter((entry) => {
    if (isGroup(entry)) return hasModuleAccess(entry.module);
    return !entry.module || hasModuleAccess(entry.module);
  });

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={onClose}
          aria-hidden
        />
      )}

      <aside
        className={`fixed left-0 top-0 z-40 h-screen bg-primary-500 text-white transition-all duration-300 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 ${isCollapsed ? 'lg:w-16' : 'lg:w-64'} ${isCollapsed ? 'w-64' : 'w-64'}`}
      >
        {/* Header sidebar — quand un logo est défini ET la sidebar est étendue,
            l'image occupe toute la largeur (100% w, sans fond blanc) et la
            hauteur est agrandie (h-24). Le bouton de repli est alors positionné
            en absolute pour ne pas réduire l'espace dédié au logo. */}
        <div
          className={`relative ${
            logoUrl && !isCollapsed ? 'h-36' : 'h-16'
          } flex items-center justify-between ${
            logoUrl && !isCollapsed ? '' : 'px-4'
          } border-b border-primary-600`}
        >
          {logoUrl ? (
            isCollapsed ? (
              <div className="flex items-center justify-center w-full">
                <img
                  src={logoUrl}
                  alt={tenantLabel}
                  title={tenantLabel}
                  className="w-8 h-8 rounded-lg object-contain bg-white flex-shrink-0"
                />
              </div>
            ) : (
              <img
                src={logoUrl}
                alt={tenantLabel}
                title={tenantLabel}
                className="w-full h-full object-contain"
              />
            )
          ) : (
            <div
              className={`flex items-center min-w-0 ${
                isCollapsed ? 'lg:justify-center lg:w-full' : ''
              }`}
            >
              <div className="w-8 h-8 rounded-lg bg-white text-primary-600 flex items-center justify-center font-bold flex-shrink-0">
                {tenantInitials}
              </div>
              {!isCollapsed && (
                <span className="ml-2 font-semibold truncate" title={tenantLabel}>
                  {tenantLabel}
                </span>
              )}
            </div>
          )}
          <button
            onClick={onClose}
            className={`lg:hidden p-1 hover:bg-primary-600 rounded ${
              logoUrl && !isCollapsed ? 'absolute top-2 right-2 bg-primary-600/40' : ''
            }`}
            aria-label="Fermer le menu"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            onClick={onToggleCollapse}
            className={`hidden lg:inline-flex p-1 hover:bg-primary-600 rounded ${
              isCollapsed
                ? 'absolute -right-3 top-5 bg-white text-primary-600 border border-gray-200 shadow'
                : logoUrl
                  ? 'absolute top-2 right-2 bg-primary-600/40'
                  : ''
            }`}
            aria-label={isCollapsed ? 'Déplier le menu' : 'Replier le menu'}
          >
            {isCollapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <ChevronLeft className="w-4 h-4" />
            )}
          </button>
        </div>

        <nav
          className="p-3 space-y-1 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={{
            maxHeight: `calc(100vh - ${logoUrl && !isCollapsed ? '9rem' : '4rem'} - 1.5rem)`,
          }}
        >
          {visible.map((entry) =>
            isGroup(entry) ? (
              <SidebarGroup
                key={entry.label}
                entry={entry}
                isCollapsed={isCollapsed}
                onClose={onClose}
                can={can}
              />
            ) : (
              <SidebarLink
                key={entry.to}
                entry={entry}
                isCollapsed={isCollapsed}
                onClose={onClose}
              />
            ),
          )}
        </nav>
      </aside>
    </>
  );
}

function SidebarLink({
  entry,
  isCollapsed,
  onClose,
}: {
  entry: LeafEntry;
  isCollapsed: boolean;
  onClose(): void;
}) {
  return (
    <div className="group relative">
      <NavLink
        to={entry.to}
        onClick={() => onClose()}
        end
        className={({ isActive }) =>
          `flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            isActive ? 'bg-white/15 text-white' : 'text-white/80 hover:bg-white/10 hover:text-white'
          } ${isCollapsed ? 'lg:justify-center' : ''}`
        }
      >
        <entry.icon className="w-5 h-5 flex-shrink-0" />
        {!isCollapsed && <span className="ml-3">{entry.label}</span>}
      </NavLink>
      {isCollapsed && (
        <span
          role="tooltip"
          className="hidden lg:group-hover:block lg:group-focus-within:block absolute left-full top-1/2 -translate-y-1/2 ml-3 z-50 whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg pointer-events-none"
        >
          {entry.label}
        </span>
      )}
    </div>
  );
}

function SidebarGroup({
  entry,
  isCollapsed,
  onClose,
  can,
}: {
  entry: GroupEntry;
  isCollapsed: boolean;
  onClose(): void;
  can(module: Module, action: Action): boolean;
}) {
  const location = useLocation();
  const isPathActive =
    location.pathname.startsWith(entry.matchPrefix) ||
    (entry.matchPaths ?? []).some(
      (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
    );
  const [open, setOpen] = useState(isPathActive);

  const visibleChildren = entry.children.filter((c) => {
    if (!c.requiredModule || !c.requiredAction) return true;
    return can(c.requiredModule, c.requiredAction);
  });

  if (isCollapsed) {
    // Quand replié : on affiche un bouton parent + tooltip avec liste enfants.
    return (
      <div className="group relative">
        <div
          className={`flex items-center justify-center px-3 py-2 rounded-lg text-sm font-medium transition-colors lg:justify-center ${
            isPathActive
              ? 'bg-white/15 text-white'
              : 'text-white/80 hover:bg-white/10 hover:text-white'
          }`}
        >
          <entry.icon className="w-5 h-5 flex-shrink-0" />
        </div>
        {/* Tooltip riche : nom + sous-liens cliquables */}
        <div className="hidden lg:group-hover:block absolute left-full top-0 ml-3 z-50 min-w-[12rem] rounded-md bg-gray-900 shadow-xl py-1.5">
          <p className="px-3 py-1.5 text-xs font-semibold text-gray-300 uppercase tracking-wide border-b border-gray-700">
            {entry.label}
          </p>
          {visibleChildren.map((c) => (
            <NavLink
              key={c.to}
              to={c.to}
              end={c.to === '/interventions' || c.to === '/reporting'}
              onClick={() => onClose()}
              className={({ isActive }) =>
                `flex items-center px-3 py-1.5 text-xs ${
                  isActive ? 'bg-white/10 text-white' : 'text-gray-100 hover:bg-white/10'
                }`
              }
            >
              <c.icon className="w-3.5 h-3.5 mr-2 flex-shrink-0" />
              {c.label}
            </NavLink>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          isPathActive
            ? 'bg-white/15 text-white'
            : 'text-white/80 hover:bg-white/10 hover:text-white'
        }`}
        aria-expanded={open}
      >
        <span className="flex items-center">
          <entry.icon className="w-5 h-5 flex-shrink-0" />
          <span className="ml-3">{entry.label}</span>
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-1 ml-3 pl-3 border-l border-white/10 space-y-0.5">
          {visibleChildren.map((c) => (
            <NavLink
              key={c.to}
              to={c.to}
              end={c.to === '/interventions' || c.to === '/reporting'}
              onClick={() => onClose()}
              className={({ isActive }) =>
                `flex items-center px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  isActive
                    ? 'bg-white/15 text-white'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`
              }
            >
              <c.icon className="w-3.5 h-3.5 flex-shrink-0 mr-2" />
              {c.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}
